import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, HandHeart, MessageCircle } from 'lucide-react';
import { BRAND, HERO_IMAGES } from '../mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete the form', description: 'Name, email and a short message are needed.' });
      return;
    }
    setBusy(true);
    try {
      await api.contact(form);
      setForm({ name: '', email: '', subject: '', message: '' });
      toast({ title: 'Thank you', description: 'Your message has been received. Grace and peace.' });
    } catch (err) {
      toast({ title: 'Could not send', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.worshipHands2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/50" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Contact Us</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">We&rsquo;d love<br />to hear from you.</h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">Reach out with a question, a testimony, or a request to partner with the ministry.</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Card className="p-8 md:p-10 bg-white rounded-2xl border border-ink/5">
              <h2 className="font-serif-display text-3xl text-ink mb-6">Send a message</h2>
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="cn">Full Name</Label>
                    <Input id="cn" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="ce">Email</Label>
                    <Input id="ce" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cs">Subject</Label>
                  <Input id="cs" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="mt-1.5" placeholder="What is this about?" />
                </div>
                <div>
                  <Label htmlFor="cm">Message</Label>
                  <Textarea id="cm" rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1.5" placeholder="Write your message…" />
                </div>
                <Button type="submit" disabled={busy} className="bg-teal-brand hover:bg-teal-deep text-white h-12 px-6 font-semibold">
                  <Send className="mr-2 h-4 w-4" /> {busy ? 'Sending…' : 'Send Message'}
                </Button>
              </form>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-7 bg-teal-deep text-white rounded-2xl">
              <h3 className="font-serif-display text-2xl mb-4">Ministry Office</h3>
              <ul className="space-y-4 text-white/85">
                <li className="flex items-start gap-3"><Mail className="h-5 w-5 text-gold-brand mt-0.5" /> {BRAND.email}</li>
                <li className="flex items-start gap-3"><Phone className="h-5 w-5 text-gold-brand mt-0.5" /> {BRAND.phone}</li>
                <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-gold-brand mt-0.5" /> {BRAND.address}</li>
              </ul>
              <Link to="/dosage" data-testid="contact-dosage-link" className="mt-5 flex items-center justify-center gap-2 w-full rounded-lg bg-[#25D366] hover:bg-[#20BA5A] text-white h-10 font-semibold transition-colors">
                <MessageCircle className="h-4 w-4" /> Read Today&rsquo;s Dosage
              </Link>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
